/**
 * 主布局：顶部 AppBar（logo + 余额 + 用户菜单）+ 左侧导航 + 内容区 Outlet。
 *
 * 桌面端常驻侧边栏，移动端折叠（temporary Drawer）。管理员可见「管理后台」分组。
 *
 * @module layouts/MainLayout
 */
import { useEffect, useState, type ReactNode, type MouseEvent } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import KeyIcon from '@mui/icons-material/Key';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import WalletIcon from '@mui/icons-material/Wallet';

import { useAuthStore } from '@/store/authStore';
import { useUserStore } from '@/store/userStore';
import { getBalance } from '@/services/user';
import { logout as logoutService } from '@/services/auth';
import MoneyText from '@/components/common/MoneyText';

/** 侧边栏宽度。 */
const DRAWER_WIDTH = 240;

/** 导航项。 */
interface NavItem {
  /** 路由。 */
  to: string;
  /** 文案。 */
  label: string;
  /** 图标。 */
  icon: ReactNode;
}

/** 普通用户导航。 */
const USER_NAV: NavItem[] = [
  { to: '/', label: '仪表盘', icon: <DashboardIcon /> },
  { to: '/apikeys', label: 'API Keys', icon: <KeyIcon /> },
  { to: '/usage', label: '用量', icon: <ShowChartIcon /> },
  { to: '/recharge', label: '充值', icon: <AccountBalanceWalletIcon /> },
  { to: '/packages', label: '套餐', icon: <CardMembershipIcon /> },
  { to: '/teams', label: '团队', icon: <GroupIcon /> },
  { to: '/refunds', label: '退款', icon: <AssignmentReturnIcon /> },
];

/** 管理员导航。 */
const ADMIN_NAV: NavItem[] = [
  { to: '/admin/channels', label: '渠道管理', icon: <AdminPanelSettingsIcon /> },
  { to: '/admin/model-mappings', label: '模型映射', icon: <AdminPanelSettingsIcon /> },
  { to: '/admin/users', label: '用户管理', icon: <AdminPanelSettingsIcon /> },
  { to: '/admin/refunds', label: '退款审核', icon: <AdminPanelSettingsIcon /> },
  { to: '/admin/configs', label: '系统配置', icon: <AdminPanelSettingsIcon /> },
];

/**
 * 主布局组件。
 *
 * @returns JSX 元素。
 */
export default function MainLayout(): JSX.Element {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const balance = useUserStore((s) => s.balance);
  const setBalance = useUserStore((s) => s.setBalance);
  const navigate = useNavigate();
  const location = useLocation();

  // 挂载时同步余额到 userStore。
  useEffect(() => {
    let active = true;
    getBalance()
      .then((res) => {
        if (active) setBalance(res.balance);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [setBalance]);

  const handleLogout = async (): Promise<void> => {
    setAnchorEl(null);
    try {
      await logoutService();
    } catch {
      /* 忽略后端登出失败，本地清理优先 */
    }
    logout();
    navigate('/login', { replace: true });
  };

  const handleMenuOpen = (e: MouseEvent<HTMLElement>): void => {
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = (): void => setAnchorEl(null);

  const isAdmin = user?.role === 'admin';

  const renderNavItem = (item: NavItem): JSX.Element => {
    const active = location.pathname === item.to;
    return (
      <ListItem key={item.to} disablePadding sx={{ mb: 0.5 }}>
        <NavLink
          to={item.to}
          end={item.to === '/'}
          style={{ textDecoration: 'none', width: '100%' }}
        >
          <ListItemButton selected={active} onClick={(): void => setMobileOpen(false)}>
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        </NavLink>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2 }}>
        <Typography variant="subtitle1" fontWeight={800} color="primary" noWrap>
          寇豆码
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, px: 1, py: 1 }}>
        {USER_NAV.map(renderNavItem)}
        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, display: 'block', mb: 0.5 }}
            >
              管理后台
            </Typography>
            {ADMIN_NAV.map(renderNavItem)}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={1}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={(): void => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            fontWeight={800}
            color="primary"
            sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
          >
            寇豆码
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Tooltip title="当前余额">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                }}
              >
                <WalletIcon fontSize="small" color="primary" />
                <MoneyText value={balance} variant="body2" fontWeight={700} />
              </Box>
            </Tooltip>
            <Tooltip title="账户菜单">
              <IconButton onClick={handleMenuOpen} size="small">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {user?.name?.[0]?.toUpperCase() ?? 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileOpen}
          onClose={(): void => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{ flexGrow: 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{user?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ pointerEvents: 'none' }}>
          <ListItemIcon>
            <WalletIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">余额：</Typography>
          <MoneyText value={balance} variant="body2" fontWeight={700} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          退出登录
        </MenuItem>
      </Menu>
    </Box>
  );
}
